import { CafeData } from "../fake-data";

const DEMO_STORAGE_KEY = "menudi-demo-db-v2";

type DemoStore = {
    _id: string;
    userName: string;
    owner: string;
    name: string;
    phoneNumber: string;
    email: string;
    password: string;
    slogan: string;
    logoUrl: string;
    posterUrl: string;
    address: string;
    workingHours: string;
    instagram: string;
    theme: string;
    language: string;
};

type DemoCategory = {
    _id: string;
    name: string;
    storeId: string;
};

type DemoProduct = {
    _id: string;
    name: string;
    description: string;
    imageUrl: string;
    categoryId: string;
    price: number;
    storeId: string;
    availability: string;
};

type DemoTable = {
    _id: string;
    storeId: string;
    tableNumber: number;
    status: string;
};

type DemoOrderItem = {
    productId: string;
    quantity: number;
};

type DemoOrder = {
    _id: string;
    storeId: string;
    tableId: string;
    takeawayType: string;
    customerPhoneNumber: string;
    deliveryAddress: string;
    courierPhoneNumber: string;
    status: string;
    createdAt: string;
    totalPrice: number;
    items: DemoOrderItem[];
};

type DemoDatabase = {
    stores: DemoStore[];
    categories: DemoCategory[];
    products: DemoProduct[];
    tables: DemoTable[];
    orders: DemoOrder[];
};

type DemoRequestConfig = {
    url: string;
    method?: string;
    data?: any;
    headers?: Record<string, string>;
};

const primaryStoreId = "store-demo-1";
const secondaryStoreId = "store-demo-2";

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

const encodeBase64 = (value: string) => {
    return window.btoa(unescape(encodeURIComponent(value)));
};

const base64UrlEncode = (value: string) =>
    encodeBase64(value).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");

const createDemoToken = (payload: Record<string, any>) => {
    const header = { alg: "HS256", typ: "JWT" };

    return [
        base64UrlEncode(JSON.stringify(header)),
        base64UrlEncode(JSON.stringify(payload)),
        "demo-signature",
    ].join(".");
};

const createId = (prefix: string) =>
    `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const createSvgDataUrl = ({
    width,
    height,
    background,
    accent,
    title,
    subtitle,
}: {
    width: number;
    height: number;
    background: string;
    accent: string;
    title: string;
    subtitle: string;
}) => {
    const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="${background}"/>
          <stop offset="100%" stop-color="#111827"/>
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" rx="28" fill="url(#g)"/>
      <circle cx="${width * 0.84}" cy="${height * 0.18}" r="${Math.max(
        24,
        Math.round(width * 0.08)
    )}" fill="${accent}" fill-opacity="0.9"/>
      <circle cx="${width * 0.12}" cy="${height * 0.8}" r="${Math.max(
        18,
        Math.round(width * 0.05)
    )}" fill="#ffffff" fill-opacity="0.12"/>
      <text x="50%" y="46%" text-anchor="middle" fill="#ffffff" font-size="${Math.round(
        width * 0.08
    )}" font-family="Arial, sans-serif" font-weight="700">${title}</text>
      <text x="50%" y="62%" text-anchor="middle" fill="#e5e7eb" font-size="${Math.round(
        width * 0.032
    )}" font-family="Arial, sans-serif">${subtitle}</text>
    </svg>`;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const createCategoryId = (categoryName: string) =>
    `cat-${categoryName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\u0600-\u06ff]+/g, "-")
        .replace(/-+/g, "-")}`;

const createSeedDatabase = (): DemoDatabase => {
    const stores: DemoStore[] = [
        {
            _id: primaryStoreId,
            userName: "demo",
            owner: "منودی",
            name: CafeData.cafeName,
            phoneNumber: "09120000000",
            email: "store@demo.local",
            password: "123456",
            slogan: "منوی دمو با داده و عکس واقعی برای ارائه به کارفرما",
            logoUrl: createSvgDataUrl({
                width: 320,
                height: 320,
                background: "#f97316",
                accent: "#fb7185",
                title: "DMO",
                subtitle: "Menudi Store",
            }),
            posterUrl: "/jonas-jacobsson-RFHFV7lVQBY-unsplash.jpg",
            address: CafeData.address,
            workingHours: "08:00 تا 23:00",
            instagram: "menudi_demo",
            theme: "دارک",
            language: "فارسی",
        },
        {
            _id: secondaryStoreId,
            userName: "roma",
            owner: "گروه رما",
            name: "رستوران روما",
            phoneNumber: "09121111111",
            email: "roma@demo.local",
            password: "123456",
            slogan: "دموی فروشگاه دوم برای نمایش پنل ادمین",
            logoUrl: createSvgDataUrl({
                width: 320,
                height: 320,
                background: "#0f766e",
                accent: "#facc15",
                title: "ROMA",
                subtitle: "Second Demo Store",
            }),
            posterUrl: createSvgDataUrl({
                width: 1600,
                height: 900,
                background: "#115e59",
                accent: "#fb7185",
                title: "Roma Kitchen",
                subtitle: "Admin can manage multiple stores",
            }),
            address: "تهران، بلوار نمونه، پلاک ۲۲",
            workingHours: "11:00 تا 24:00",
            instagram: "roma_demo",
            theme: "دارک",
            language: "فارسی",
        },
    ];

    const categories: DemoCategory[] = CafeData.categories
        .filter((category) => category !== "همه")
        .map((category) => ({
            _id: createCategoryId(category),
            storeId: primaryStoreId,
            name: category,
        }));

    categories.push({ _id: "cat-main", storeId: secondaryStoreId, name: "غذای اصلی" });

    const products: DemoProduct[] = CafeData.menu.map((item, index) => ({
        _id: `prd-${item.id}-${index + 1}`,
        storeId: primaryStoreId,
        categoryId: createCategoryId(item.category),
        name: item.name,
        description: item.description,
        imageUrl: item.fileUrl,
        price: item.price * 10,
        availability: "available",
    }));

    products.push(
        {
            _id: "prd-burger",
            storeId: secondaryStoreId,
            categoryId: "cat-main",
            name: "برگر ویژه",
            description: "برگر دست ساز با پنیر چدار و سس اختصاصی.",
            imageUrl: createSvgDataUrl({
                width: 1200,
                height: 800,
                background: "#7f1d1d",
                accent: "#fbbf24",
                title: "Signature Burger",
                subtitle: "Second store sample",
            }),
            price: 245000,
            availability: "available",
        }
    );

    const tables: DemoTable[] = [1, 2, 3, 4, 5, 6].map((tableNumber) => ({
        _id: `tbl-${tableNumber}`,
        storeId: primaryStoreId,
        tableNumber,
        status: tableNumber === 2 ? "waiting_for_order" : "empty",
    }));

    const orders: DemoOrder[] = [
        {
            _id: "ord-1",
            storeId: primaryStoreId,
            tableId: "tbl-2",
            takeawayType: "none",
            customerPhoneNumber: "09123334444",
            deliveryAddress: "",
            courierPhoneNumber: "",
            status: "ordered",
            createdAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
            totalPrice: 255000,
            items: [
                { productId: "prd-94pg2-12", quantity: 1 },
                { productId: "prd-75gt3-16", quantity: 1 },
            ],
        },
        {
            _id: "ord-2",
            storeId: primaryStoreId,
            tableId: "",
            takeawayType: "pickup",
            customerPhoneNumber: "09125556666",
            deliveryAddress: "",
            courierPhoneNumber: "",
            status: "ready",
            createdAt: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
            totalPrice: 170000,
            items: [{ productId: "prd-72bc5-1", quantity: 2 }],
        },
    ];

    return {
        stores,
        categories,
        products,
        tables,
        orders,
    };
};

const canUseStorage = () => typeof window !== "undefined" && !!window.localStorage;

const readDatabase = (): DemoDatabase => {
    if (!canUseStorage()) {
        return createSeedDatabase();
    }

    const rawDatabase = window.localStorage.getItem(DEMO_STORAGE_KEY);

    if (!rawDatabase) {
        const seeded = createSeedDatabase();
        window.localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(seeded));
        return seeded;
    }

    try {
        const parsed = JSON.parse(rawDatabase);

        if (
            Array.isArray(parsed?.stores) &&
            Array.isArray(parsed?.categories) &&
            Array.isArray(parsed?.products) &&
            Array.isArray(parsed?.tables) &&
            Array.isArray(parsed?.orders)
        ) {
            return parsed;
        }
    } catch (error) {
        console.error("Failed to parse demo database", error);
    }

    const fallback = createSeedDatabase();
    window.localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(fallback));
    return fallback;
};

const writeDatabase = (database: DemoDatabase) => {
    if (!canUseStorage()) {
        return;
    }

    window.localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(database));
};

export const ensureDemoData = () => {
    readDatabase();
};

const resolveStore = (database: DemoDatabase, identify: string) =>
    database.stores.find(
        (store) => store._id === identify || store.userName === identify
    );

const resolveStoreId = (database: DemoDatabase, identify: string) =>
    resolveStore(database, identify)?._id || identify;

const hydrateProduct = (database: DemoDatabase, product: DemoProduct | undefined) => {
    if (!product) {
        return null;
    }

    const category = database.categories.find((item) => item._id === product.categoryId);

    return {
        ...clone(product),
        categoryId: category ? clone(category) : null,
    };
};

const getStoreProductsData = (identify: string, categoryId?: string) => {
    const database = readDatabase();
    const storeId = resolveStoreId(database, identify);

    return database.products
        .filter(
            (product) =>
                product.storeId === storeId && (!categoryId || product.categoryId === categoryId)
        )
        .map((product) => hydrateProduct(database, product));
};

const hydrateOrder = (database: DemoDatabase, order: DemoOrder) => ({
    ...clone(order),
    items: order.items
        .map((item) => {
            const product = hydrateProduct(
                database,
                database.products.find((productItem) => productItem._id === item.productId)
            );

            if (!product) {
                return null;
            }

            return {
                ...clone(item),
                productId: product,
            };
        })
        .filter(Boolean),
});

const sanitizeUserName = (value: string) => {
    const normalized = value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9-_]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");

    return normalized || `store-${Date.now().toString(36)}`;
};

const fileToDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(`${reader.result || ""}`);
        reader.onerror = () => reject(new Error("آپلود فایل انجام نشد"));
        reader.readAsDataURL(file);
    });

const buildSession = (role: "admin" | "user", userId?: string) => {
    const exp = Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 30;
    const token = createDemoToken({
        sub: userId || role,
        role,
        exp,
    });

    return {
        accessToken: token,
        accessTokenExpiration: `${exp}`,
        userId: userId || "admin-demo",
        role,
    };
};

export const createDemoSession = (role: "admin" | "user") =>
    role === "admin" ? buildSession("admin") : buildSession("user", primaryStoreId);

const loginWithCredentials = (email: string, password: string) => {
    const database = readDatabase();

    if (
        (email === "admin" || email === "admin@demo.local") &&
        password === "123456"
    ) {
        return buildSession("admin");
    }

    const store = database.stores.find(
        (item) => item.email === email && item.password === password
    );

    if (!store) {
        throw new Error("کاربر دمو پیدا نشد");
    }

    return buildSession("user", store._id);
};

const updateStore = (storeId: string, patch: Partial<DemoStore>) => {
    const database = readDatabase();
    const storeIndex = database.stores.findIndex((item) => item._id === storeId);

    if (storeIndex === -1) {
        throw new Error("فروشگاه پیدا نشد");
    }

    database.stores[storeIndex] = {
        ...database.stores[storeIndex],
        ...patch,
        userName: sanitizeUserName(patch.userName || database.stores[storeIndex].userName),
    };

    writeDatabase(database);

    return clone(database.stores[storeIndex]);
};

const createStore = (payload: Partial<DemoStore>) => {
    const database = readDatabase();
    const nextStoreId = createId("store");
    const userName = sanitizeUserName(payload.userName || payload.name || nextStoreId);

    const newStore: DemoStore = {
        _id: nextStoreId,
        userName,
        owner: payload.owner || "",
        name: payload.name || "فروشگاه جدید",
        phoneNumber: payload.phoneNumber || "",
        email: payload.email || `${userName}@demo.local`,
        password: payload.password || "123456",
        slogan: payload.slogan || "فروشگاه دمو تازه ساخته شده",
        logoUrl:
            payload.logoUrl ||
            createSvgDataUrl({
                width: 320,
                height: 320,
                background: "#1d4ed8",
                accent: "#fb7185",
                title: userName.toUpperCase().slice(0, 5),
                subtitle: "New Demo Store",
            }),
        posterUrl:
            payload.posterUrl ||
            createSvgDataUrl({
                width: 1600,
                height: 900,
                background: "#0f172a",
                accent: "#38bdf8",
                title: payload.name || "New Store",
                subtitle: "Created locally for demo",
            }),
        address: payload.address || "",
        workingHours: payload.workingHours || "",
        instagram: payload.instagram || "",
        theme: payload.theme || "دارک",
        language: payload.language || "فارسی",
    };

    database.stores.unshift(newStore);
    database.tables.push(
        ...[1, 2, 3, 4].map((tableNumber) => ({
            _id: createId("table"),
            storeId: nextStoreId,
            tableNumber,
            status: "empty",
        }))
    );
    writeDatabase(database);

    return clone(newStore);
};

const createCategory = (name: string, storeId: string) => {
    const database = readDatabase();
    const newCategory: DemoCategory = {
        _id: createId("category"),
        name,
        storeId,
    };

    database.categories.push(newCategory);
    writeDatabase(database);

    return clone(newCategory);
};

const updateCategory = (categoryId: string, name: string) => {
    const database = readDatabase();
    const category = database.categories.find((item) => item._id === categoryId);

    if (!category) {
        throw new Error("دسته بندی پیدا نشد");
    }

    category.name = typeof name === "string" ? name : `${name}`;
    writeDatabase(database);

    return clone(category);
};

const deleteCategory = (categoryId: string) => {
    const database = readDatabase();
    database.categories = database.categories.filter((item) => item._id !== categoryId);
    database.products = database.products.filter((item) => item.categoryId !== categoryId);
    writeDatabase(database);

    return { success: true };
};

const createProduct = (payload: Partial<DemoProduct>) => {
    const database = readDatabase();
    const newProduct: DemoProduct = {
        _id: createId("product"),
        name: payload.name || "آیتم جدید",
        description: payload.description || "",
        imageUrl:
            payload.imageUrl ||
            createSvgDataUrl({
                width: 1200,
                height: 800,
                background: "#1f2937",
                accent: "#f97316",
                title: payload.name || "Menu Item",
                subtitle: "Created locally",
            }),
        categoryId: `${payload.categoryId || ""}`,
        price: Number(payload.price || 0),
        storeId: `${payload.storeId || primaryStoreId}`,
        availability: payload.availability || "available",
    };

    database.products.unshift(newProduct);
    writeDatabase(database);

    return hydrateProduct(database, newProduct);
};

const updateProduct = (productId: string, payload: Partial<DemoProduct>) => {
    const database = readDatabase();
    const productIndex = database.products.findIndex((item) => item._id === productId);

    if (productIndex === -1) {
        throw new Error("آیتم منو پیدا نشد");
    }

    database.products[productIndex] = {
        ...database.products[productIndex],
        ...payload,
        price: Number(payload.price ?? database.products[productIndex].price),
        categoryId: `${payload.categoryId ?? database.products[productIndex].categoryId}`,
    };

    writeDatabase(database);

    return hydrateProduct(database, database.products[productIndex]);
};

const deleteProduct = (productId: string) => {
    const database = readDatabase();
    database.products = database.products.filter((item) => item._id !== productId);
    database.orders = database.orders.map((order) => ({
        ...order,
        items: order.items.filter((item) => item.productId !== productId),
    }));
    writeDatabase(database);

    return { success: true };
};

const createTable = (storeId: string, tableNumber: number) => {
    const database = readDatabase();
    const newTable: DemoTable = {
        _id: createId("table"),
        storeId,
        tableNumber,
        status: "empty",
    };

    database.tables.push(newTable);
    writeDatabase(database);

    return clone(newTable);
};

const createOrder = (payload: any) => {
    const database = readDatabase();
    const rawItems = Array.isArray(payload?.items) ? payload.items : [];
    const items = rawItems
        .map((item) => ({
            productId: `${item.productId}`,
            quantity: Number(item.quantity || 0),
        }))
        .filter((item) => item.productId && item.quantity > 0);

    const totalPrice = items.reduce((sum, item) => {
        const product = database.products.find((productItem) => productItem._id === item.productId);
        return sum + (product ? product.price * item.quantity : 0);
    }, 0);

    const newOrder: DemoOrder = {
        _id: createId("order"),
        storeId: `${payload.storeId || primaryStoreId}`,
        tableId: `${payload.tableId || ""}`,
        takeawayType: payload.takeawayType || "none",
        customerPhoneNumber: payload.customerPhoneNumber || "",
        deliveryAddress: payload.deliveryAddress || "",
        courierPhoneNumber: payload.courierPhoneNumber || "",
        status: "ordered",
        createdAt: new Date().toISOString(),
        totalPrice,
        items,
    };

    database.orders.unshift(newOrder);

    if (newOrder.takeawayType === "none" && newOrder.tableId) {
        const table = database.tables.find((item) => item._id === newOrder.tableId);
        if (table) {
            table.status = "waiting_for_order";
        }
    }

    writeDatabase(database);

    return hydrateOrder(database, newOrder);
};

const getFileFromFormData = (formData: FormData) => {
    const file = formData.get("image");

    if (!(file instanceof File)) {
        throw new Error("فایل انتخاب نشده است");
    }

    return file;
};

const normalizeUrl = (url: string) => {
    const cleanedUrl = url.replace(/^https?:\/\/[^/]+/i, "");
    return cleanedUrl.startsWith("/") ? cleanedUrl : `/${cleanedUrl}`;
};

const parsePath = (url: string) => {
    const normalizedUrl = normalizeUrl(url);
    const [pathname, queryString = ""] = normalizedUrl.split("?");
    return {
        pathname,
        params: new URLSearchParams(queryString),
    };
};

export const demoRequest = async (config: DemoRequestConfig): Promise<any> => {
    ensureDemoData();

    const method = (config.method || "get").toLowerCase();
    const { pathname, params } = parsePath(config.url);

    if (method === "get" && pathname === "/admin/stores") {
        return { status: 200, data: clone(readDatabase().stores) };
    }

    if (method === "get" && pathname.startsWith("/store/")) {
        const identify = decodeURIComponent(pathname.replace("/store/", ""));
        const store = resolveStore(readDatabase(), identify);

        if (!store) {
            throw new Error("فروشگاه پیدا نشد");
        }

        return { status: 200, data: clone(store) };
    }

    if (method === "patch" && pathname.startsWith("/store/")) {
        const storeId = decodeURIComponent(pathname.replace("/store/", ""));
        return { status: 200, data: updateStore(storeId, config.data || {}) };
    }

    if (method === "post" && pathname === "/store/login") {
        return { status: 200, data: loginWithCredentials(config.data?.email, config.data?.password) };
    }

    if (method === "post" && pathname === "/admin/register") {
        return { status: 201, data: createStore(config.data || {}) };
    }

    if (method === "get" && /\/product\/[^/]+\/list$/.test(pathname)) {
        const identify = decodeURIComponent(pathname.split("/")[2] || "");
        const categoryId = params.get("categoryId") || undefined;
        return {
            status: 200,
            data: getStoreProductsData(identify, categoryId),
        };
    }

    if (method === "get" && pathname.startsWith("/categories/")) {
        const identify = decodeURIComponent(pathname.replace("/categories/", ""));
        const database = readDatabase();
        const storeId = resolveStoreId(database, identify);
        return {
            status: 200,
            data: clone(database.categories.filter((item) => item.storeId === storeId)),
        };
    }

    if (method === "post" && pathname === "/categories/create") {
        return {
            status: 201,
            data: createCategory(config.data?.name, config.data?.storeId),
        };
    }

    if (method === "put" && pathname.startsWith("/categories/")) {
        const categoryId = decodeURIComponent(pathname.replace("/categories/", ""));
        return { status: 200, data: updateCategory(categoryId, config.data) };
    }

    if (method === "delete" && pathname.startsWith("/categories/")) {
        const categoryId = decodeURIComponent(pathname.replace("/categories/", ""));
        return { status: 200, data: deleteCategory(categoryId) };
    }

    if (method === "get" && pathname.startsWith("/product/")) {
        const segments = pathname.split("/").filter(Boolean);
        if (segments.length === 2) {
            const productId = decodeURIComponent(segments[1]);
            const product = hydrateProduct(
                readDatabase(),
                readDatabase().products.find((item) => item._id === productId)
            );

            if (!product) {
                throw new Error("آیتم پیدا نشد");
            }

            return { status: 200, data: product };
        }
    }

    if (method === "post" && pathname === "/product/create") {
        return { status: 201, data: createProduct(config.data || {}) };
    }

    if (method === "put" && pathname.startsWith("/product/")) {
        const productId = decodeURIComponent(pathname.replace("/product/", ""));
        return { status: 200, data: updateProduct(productId, config.data || {}) };
    }

    if (method === "delete" && pathname.startsWith("/product/")) {
        const productId = decodeURIComponent(pathname.replace("/product/", ""));
        return { status: 200, data: deleteProduct(productId) };
    }

    if (method === "get" && pathname.startsWith("/table/all/")) {
        const identify = decodeURIComponent(pathname.replace("/table/all/", ""));
        const database = readDatabase();
        const storeId = resolveStoreId(database, identify);
        return {
            status: 200,
            data: {
                tables: clone(database.tables.filter((item) => item.storeId === storeId)),
            },
        };
    }

    if (method === "post" && pathname === "/table/add") {
        return {
            status: 201,
            data: createTable(config.data?.storeId, Number(config.data?.tableNumber || 1)),
        };
    }

    if (method === "get" && pathname.startsWith("/order/")) {
        const storeId = decodeURIComponent(pathname.replace("/order/", ""));
        const database = readDatabase();
        return {
            status: 200,
            data: {
                orders: database.orders
                    .filter((item) => item.storeId === storeId)
                    .map((order) => hydrateOrder(database, order)),
            },
        };
    }

    if (method === "post" && pathname === "/order/create") {
        return {
            status: 201,
            data: createOrder(config.data || {}),
        };
    }

    if (method === "post" && pathname === "/file/upload") {
        const imageFile = getFileFromFormData(config.data);
        const imageDataUrl = await fileToDataUrl(imageFile);
        return {
            status: 201,
            data: {
                image: {
                    _id: imageDataUrl,
                },
            },
        };
    }

    if (method === "delete" && pathname.startsWith("/file/delete/")) {
        return { status: 200, data: { success: true } };
    }

    throw new Error(`Demo endpoint not implemented: ${method.toUpperCase()} ${pathname}`);
};